import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type {
  PrizeHistoryItem,
  RouletteDetail,
  RoulettePrize,
  SpinResult,
} from '@/types/roulette';
import { RouletteSpinError } from '~/adapters/api-roulette/roulette-api';
import { fetchRoulettesHandler } from '~/handlers/fetch-roulettes.handler';
import { fetchRouletteDetailHandler } from '~/handlers/fetch-roulette-detail.handler';
import { postRouletteSpinHandler } from '~/handlers/post-roulette-spin.handler';
import { fetchRoulettePrizeHistoryHandler } from '~/handlers/fetch-roulette-prize-history.handler';
import { usePlayerProfileStore } from '~/stores/player-profile';
import { GAMIFICATION_SLUGS } from '~/types/player-profile';
import { tryCatch } from '~/utils/apostatudo/try-catch';

// 'unavailable' = o core respondeu, e não há roleta pro segmento deste jogador
// (lista vazia). 'ineligible' = há roleta, mas não pra esta conta ainda: abaixo
// do nível CRM mínimo (`gamifications.roulette.is_eligible: false`).
//
// São separados porque a COPY é outra — "nenhuma roleta disponível pra você" fala
// de catálogo, e pra quem está fora da promoção o assunto é a promoção, não o
// catálogo. Os dois seguem sendo respostas legítimas, e nenhum oferece "tentar
// novamente" (diferente de 'error'): não há o que tentar.
export type RouletteStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'spinning'
  | 'error'
  | 'unavailable'
  | 'ineligible';

export type RouletteHistoryStatus =
  | 'idle'
  | 'loading'
  | 'loadingMore'
  | 'ready'
  | 'error';

export const useRouletteStore = defineStore('roulette', () => {
  const authStore = useAuthStore();
  const { openLogin } = useAuth();
  const { userId } = useUserData();
  const playerProfileStore = usePlayerProfileStore();

  // Roletinha exige o nível CRM mínimo dela (`gamifications.roulette` do core,
  // via /players/me). Inelegível não gasta a busca da lista — cai direto no
  // aviso de nível, do mesmo jeito que o check-in e o promocode.
  //
  // Só o veredito FRESCO do core nega (`isKnownIneligibleFor`): num "não sei" —
  // perfil ainda em trânsito, requisição falhada, core offline, mapa quebrado —
  // quem responde é a lista do core, como antes. Esconder um item de menu por
  // falta de resposta é seguro (não convida ninguém); NEGAR a abertura por falta
  // de resposta não é, porque tiraria a roleta de quem tem direito a ela.
  const isKnownIneligible = computed(() =>
    playerProfileStore.isKnownIneligibleFor(GAMIFICATION_SLUGS.roulette),
  );

  // Abertura do modal global (gatilho via store, sem navegar de rota).
  const isOpen = ref(false);

  // Slug da roleta do core já resolvido no open() (antes do sheet abrir). Quando
  // setado, o init() carrega o detalhe direto por ele, sem refazer a busca da
  // lista. Evita fetch duplicado no fluxo de abertura.
  const pendingSlug = ref<string | null>(null);

  const status = ref<RouletteStatus>('idle');
  const detail = ref<RouletteDetail | null>(null);
  const errorMessage = ref<string | null>(null);

  const lastSpinResult = ref<SpinResult | null>(null);

  // Histórico de prêmios (paginado).
  const historyItems = ref<PrizeHistoryItem[]>([]);
  const historyStatus = ref<RouletteHistoryStatus>('idle');
  const historyPage = ref(0);
  const historyLastPage = ref(1);
  const canLoadMoreHistory = computed(
    () => historyPage.value < historyLastPage.value,
  );

  const prizes = computed<RoulettePrize[]>(() =>
    [...(detail.value?.prizes ?? [])].sort((a, b) => a.position - b.position),
  );

  const availableSpins = computed(() => detail.value?.availableSpins ?? 0);
  const nextSpinAvailableAt = computed(
    () => detail.value?.nextSpinAvailableAt ?? null,
  );

  const slug = computed(() => detail.value?.slug ?? '');
  const canSpin = computed(
    () => status.value === 'ready' && availableSpins.value > 0 && !!slug.value,
  );

  function reset() {
    status.value = 'idle';
    detail.value = null;
    errorMessage.value = null;
    lastSpinResult.value = null;
    pendingSlug.value = null;
    historyItems.value = [];
    historyStatus.value = 'idle';
    historyPage.value = 0;
    historyLastPage.value = 1;
  }

  async function open() {
    // Deslogado não abre o sheet da roleta: só o login. Evita o modal da
    // roletinha aparecer por cima do login em deep-links (ex.: banner
    // /mini-games/12472), já que init() exige auth de qualquer forma.
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    pendingSlug.value = null;

    // Jogador que o core já disse que não participa: abre no aviso de fora da
    // promoção sem pedir a lista. O init() (no after-enter do slide-up) respeita
    // o estado e não refaz o fetch.
    if (isKnownIneligible.value) {
      status.value = 'ineligible';
      isOpen.value = true;

      return;
    }

    // Resolve a roleta ANTES de abrir o sheet, aproveitando o tempo da animação
    // de slide-up. Só dá pra decidir isso com o userId; sem ele (ou se a lista
    // falhar), abre o sheet e deixa o init() tratar — comportamento de antes.
    if (userId.value) {
      status.value = 'loading';

      const [list] = await tryCatch(
        fetchRoulettesHandler({ userId: userId.value }),
      );

      const first = list?.data[0];

      if (list && !first) {
        // Sem roleta no core: abre o sheet mesmo assim com o aviso de
        // indisponível (o init() no after-enter respeita e não refaz o fetch).
        status.value = 'unavailable';
      }

      // Roleta do core resolvida: guarda o slug pra o init() (no after-enter do
      // slide-up) carregar o detalhe sem refazer a busca da lista.
      if (first) {
        pendingSlug.value = first.slug;
      }
    }

    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
  }

  function captureUnexpected(error: unknown, source: string) {
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      { tags: { source } },
    );
  }

  // Resolve a roleta do usuário: lista do segmento → data[0]. Sem itens ⇒ indisponível.
  async function init() {
    if (!authStore.isAuthenticated) {
      openLogin();

      return;
    }

    if (!userId.value) {
      // autenticado, mas o perfil ainda não carregou (GET /auth/user-profile)
      status.value = 'error';
      errorMessage.value =
        'Não foi possível carregar seus dados. Tente novamente.';

      return;
    }

    // O open() já resolveu que não há o que abrir pro user: o sheet abriu direto
    // no aviso — refazer o fetch aqui só causaria o flicker "Carregando..." →
    // aviso. (Nenhum dos dois estados chega aqui por outro caminho: fechar o
    // sheet passa por reset(), e retry/refresh exigem 'error'/'ready'.)
    if (status.value === 'unavailable' || status.value === 'ineligible') {
      return;
    }

    // Mesmo gate do open(), pro retry da tela de erro (`@click="store.init()"`)
    // não voltar a pedir a lista de quem o core já disse que não participa.
    if (isKnownIneligible.value) {
      status.value = 'ineligible';

      return;
    }

    // Roleta já resolvida no open() (antes do sheet abrir): carrega o detalhe
    // direto pelo slug, sem refazer a busca da lista.
    if (pendingSlug.value) {
      const targetSlug = pendingSlug.value;

      pendingSlug.value = null;
      status.value = 'loading';
      errorMessage.value = null;
      await loadDetail(targetSlug);

      return;
    }

    status.value = 'loading';
    errorMessage.value = null;

    const [list, listError] = await tryCatch(
      fetchRoulettesHandler({ userId: userId.value }),
    );

    if (listError || !list) {
      status.value = 'error';
      errorMessage.value = 'Serviço indisponível no momento. Tente novamente.';
      captureUnexpected(listError, 'roulette.init.list');

      return;
    }

    const first = list.data[0];

    if (!first) {
      // Sem roleta no core: aviso de indisponível dentro do próprio sheet.
      // (Erro de rede/serviço acima segue na tela de erro — isso aqui é só o
      // caso legítimo de "user sem roleta".)
      status.value = 'unavailable';

      return;
    }

    await loadDetail(first.slug);
  }

  // silent: refresh de fundo (inatividade/retomada de aba). Uma falha transitória
  // não pode derrubar o modal pra tela de erro — mantém o último detalhe bom.
  async function loadDetail(
    targetSlug: string,
    options?: { silent?: boolean },
  ) {
    if (!userId.value) return;

    const [data, detailError] = await tryCatch(
      fetchRouletteDetailHandler({ slug: targetSlug, userId: userId.value }),
    );

    if (detailError || !data) {
      if (options?.silent) return;

      status.value = 'error';
      errorMessage.value = 'Serviço indisponível no momento. Tente novamente.';
      captureUnexpected(detailError, 'roulette.loadDetail');

      return;
    }

    detail.value = data;
    status.value = 'ready';
  }

  // Gira a roleta. Retorna o resultado para o componente animar a roda até a
  // position e só então abrir o popup. O sorteio é server-authoritative.
  async function spin(): Promise<SpinResult | null> {
    if (!canSpin.value || !userId.value) return null;

    status.value = 'spinning';
    errorMessage.value = null;

    const [result, spinError] = await tryCatch(
      postRouletteSpinHandler({ slug: slug.value, userId: userId.value }),
    );

    if (spinError || !result) {
      status.value = 'ready';

      // O adapter classifica a falha (kind); mensagem e triagem decidem aqui.
      const spinErr = spinError instanceof RouletteSpinError ? spinError : null;

      errorMessage.value =
        spinErr?.kind === 'rate_limited'
          ? 'Muitas tentativas. Aguarde um momento e tente novamente.'
          : spinErr?.kind === 'network'
            ? 'Não foi possível conectar. Tente novamente.'
            : spinErr?.message || 'Erro ao girar a roleta.';

      // 429, queda de rede e delivery_failed (giro estornado, retryável) são
      // esperados; só erro da API/desconhecido interessa à triagem do Sentry.
      if (!spinErr || spinErr.kind === 'api') {
        captureUnexpected(spinError, 'roulette.spin');
      }

      return null;
    }

    lastSpinResult.value = result;

    if (detail.value) {
      detail.value.availableSpins = result.spinsRemaining;
      // Entrega síncrona: o /spin já devolve o próximo horário — atualiza o
      // countdown na hora (sem esperar o refetch do detalhe).
      detail.value.nextSpinAvailableAt = result.nextSpinAvailableAt;
    }

    return result;
  }

  // Chamado pelo componente após a animação da roda terminar.
  function settleSpin() {
    status.value = 'ready';
  }

  // Histórico de prêmios (all-time, paginado). loadHistory recarrega a 1ª página.
  async function loadHistory() {
    if (!userId.value) return;

    historyStatus.value = 'loading';

    const [page, error] = await tryCatch(
      fetchRoulettePrizeHistoryHandler({ userId: userId.value, page: 1 }),
    );

    if (error || !page) {
      historyStatus.value = 'error';
      captureUnexpected(error, 'roulette.history');

      return;
    }

    historyItems.value = page.items;
    historyPage.value = page.currentPage;
    historyLastPage.value = page.lastPage;
    historyStatus.value = 'ready';
  }

  async function loadMoreHistory() {
    if (
      !userId.value ||
      !canLoadMoreHistory.value ||
      historyStatus.value === 'loadingMore'
    ) {
      return;
    }

    historyStatus.value = 'loadingMore';

    const [page, error] = await tryCatch(
      fetchRoulettePrizeHistoryHandler({
        userId: userId.value,
        page: historyPage.value + 1,
      }),
    );

    if (error || !page) {
      historyStatus.value = 'ready';

      return;
    }

    historyItems.value = [...historyItems.value, ...page.items];
    historyPage.value = page.currentPage;
    historyLastPage.value = page.lastPage;
    historyStatus.value = 'ready';
  }

  return {
    isOpen,
    status,
    detail,
    errorMessage,
    lastSpinResult,
    prizes,
    availableSpins,
    nextSpinAvailableAt,
    slug,
    canSpin,
    historyItems,
    historyStatus,
    canLoadMoreHistory,
    open,
    close,
    init,
    loadDetail,
    spin,
    settleSpin,
    loadHistory,
    loadMoreHistory,
    reset,
  };
});
